/**
 * `/` — auth-aware entry point.
 *
 *   - auth unresolved  → SPLASH (never redirect; PWA cold-start lands
 *                         here before /me has confirmed the session)
 *   - authenticated    → /boards (default) or /dashboard when the
 *                         founder has opted into "Use dashboard as
 *                         landing" via Settings → Profile (Phase 12).
 *   - unauthenticated  → /login (only once the session is *confirmed*
 *                         absent — a guest is never stranded on splash)
 *
 * Why a splash instead of an eager /login redirect (item 12, 2026-06-11):
 * AuthBootstrap flips the store idle → loading → authenticated/
 * unauthenticated *asynchronously* (it awaits GET /api/auth/me). A PWA
 * cold-start mounts the router before /me resolves, so the entry route's
 * synchronous `beforeLoad` sees a non-terminal status. The old code
 * redirected to /login unconditionally in that window, kicking already
 * logged-in users to the sign-in screen. We now mirror the (correct)
 * `_authenticated` guard: only act on a *terminal* status, and while the
 * status is still idle/loading we render a splash and let the component
 * reactively redirect once the store settles.
 *
 * Founder mandate (2026-05-15): post-login lands on actual content,
 * never a marketing screen. The landing preference is per-device in
 * localStorage — no migration needed, no DB write on every signin.
 */
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

import { useAuthStore } from '../stores/auth-store';

const LANDING_KEY = 'chaos-landing-preference';

function preferredLanding(): '/boards' | '/dashboard' {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LANDING_KEY) : null;
    if (raw === 'dashboard') return '/dashboard';
  } catch {
    /* ignore */
  }
  return '/boards';
}

export const Route = createFileRoute('/')({
  // Fast path: if the session is *already* resolved at navigation time
  // (e.g. an in-app link to `/`, or a reload after the store rehydrated),
  // redirect synchronously so we never flash the splash. For a
  // non-terminal status we fall through to the component, which shows the
  // splash and redirects reactively once AuthBootstrap settles.
  beforeLoad: () => {
    const status = useAuthStore.getState().status;
    if (status === 'authenticated') {
      throw redirect({ to: preferredLanding() });
    }
    if (status === 'unauthenticated') {
      throw redirect({ to: '/login' });
    }
  },
  component: EntrySplash,
});

function EntrySplash() {
  const navigate = useNavigate();
  // Reactive subscription: re-renders when AuthBootstrap flips the store.
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status === 'authenticated') {
      void navigate({ to: preferredLanding(), replace: true });
    } else if (status === 'unauthenticated') {
      // CRITICAL: a confirmed-unauthenticated guest must still reach
      // /login — never left stranded on the splash forever.
      void navigate({ to: '/login', replace: true });
    }
    // While idle/loading we intentionally do nothing and keep the splash.
  }, [status, navigate]);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background text-foreground"
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export const __testing = { LANDING_KEY };
